<template>
  <div class="fill-height">
    <!-- Overlay loading: blok semua interaksi selama request berjalan -->
    <v-overlay :value="busy" z-index="1000">
      <div class="text-center">
        <v-progress-circular indeterminate size="48" color="white"></v-progress-circular>
        <div class="body-1 mt-4 white--text">Memproses data, jangan tutup halaman...</div>
      </div>
    </v-overlay>

    <section>
      <v-row class="pa-4 pb-2 pr-1">
        <v-col cols="12">
          <div class="d-flex align-center">
            <v-avatar tile size="30">
              <v-img contain alt="grip" :src="require('@/assets/image/grip_menu.png')"></v-img>
            </v-avatar>
            <span class="title black--text ml-2" style="font-weight: 600;">Import Anggaran DPA</span>
          </div>
          <div class="body-2 grey--text text--darken-1 mt-1">
            Program › Kegiatan › Sub Kegiatan semua OPD. Menu ini hanya tampil untuk role admin.
          </div>
        </v-col>
      </v-row>

      <v-row class="pa-4 pr-1">
        <v-col cols="12" class="py-0">
          <!-- FORM -->
          <v-card flat class="mb-4" style="border-radius: 10px; border: 1px solid #E7E7E7;">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="body-2 mb-2"><span class="red--text">*</span> Tahun Anggaran</div>
                  <v-select filled rounded dense hide-details :color="set_color" :items="years"
                    v-model="form.tahun" :disabled="busy"></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="body-2 mb-2"><span class="red--text">*</span> Status DPA</div>
                  <v-select filled rounded dense :color="set_color" :items="dpaStatuses"
                    v-model="form.dpa_status" :disabled="busy" hint="murni / p1 / perubahan"
                    persistent-hint></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="body-2 mb-2">Bulan Mulai DPA (opsional)</div>
                  <v-select filled rounded dense :color="set_color" :items="bulanItems"
                    v-model="form.bulan_mulai" :disabled="busy || form.dpa_status === 'murni'"
                    hint="Realisasi bulan sebelum ini disalin dari DPA sebelumnya"
                    persistent-hint></v-select>
                </v-col>
              </v-row>

              <div class="body-2 mb-2 mt-4"><span class="red--text">*</span> File Excel (.xlsx / .xls)</div>
              <div
                class="pa-6 text-center cursor-pointer"
                :style="`border: 2px dashed ${dragging ? set_color : '#A9C4F5'}; border-radius: 10px; background: #F4F6FF;`"
                @click="!busy && $refs.fileInput.click()"
                @dragover.prevent="dragging = true"
                @dragleave.prevent="dragging = false"
                @drop.prevent="onDrop">
                <input ref="fileInput" type="file" accept=".xlsx,.xls" style="display: none;"
                  @change="onPick" />
                <div class="body-2 blue--text text--darken-2">
                  Tarik file ke sini atau klik untuk memilih
                </div>
                <div v-if="file" class="body-2 mt-1">{{ file.name }} · {{ fileSize }}</div>
              </div>

              <div class="d-flex align-center mt-4">
                <v-btn outlined :disabled="busy" class="text-capitalize" @click="unduhTemplate">
                  <v-icon size="18" class="mr-1">mdi-download</v-icon> Unduh Template
                </v-btn>
                <v-spacer />
                <v-btn color="primary" elevation="0" class="text-capitalize"
                  :disabled="busy || !canSubmit" @click="runDryRun">
                  Cek Data (Dry Run)
                </v-btn>
              </div>

              <v-alert v-if="message.error" type="error" dense text class="mt-4 mb-0">
                {{ message.error }}
              </v-alert>

              <div class="caption grey--text text--darken-1 mt-4">
                Data dicocokkan per kode (unit + tahun + status DPA + level + kode), bukan nama:
                sudah ada → update, belum ada → insert. Import ulang file yang sama aman.
                <code>dihapus_dpa = ya</code> menandai task dihapus dari DPA (baris tetap ada).
                Untuk tiap unit di Excel, task lama yang kodenya tidak ada di Excel ikut ditandai
                dihapus. Import bersifat semua-atau-tidak: satu error membatalkan seluruhnya.
              </div>
            </v-card-text>
          </v-card>

          <!-- HASIL -->
          <v-card v-if="result" flat class="mb-4" style="border-radius: 10px; border: 1px solid #E7E7E7;">
            <v-card-text>
              <div class="title mb-1" style="font-weight: 600;">
                {{ result.dry_run ? 'Hasil Cek Data (Dry Run)' : 'Import Berhasil' }}
                · {{ result.tahun }}-{{ result.dpa_status }}
              </div>
              <div class="body-2 grey--text text--darken-1 mb-3">
                <span v-if="result.dry_run">Belum ada data yang diubah. </span>
                Durasi {{ result.durasi_detik }} detik · {{ fmt(result.total_baris) }} baris ·
                {{ fmt(result.baris_valid) }} valid · {{ fmt(result.jumlah_unit) }} unit
                <span v-if="result.dpa_sebelumnya"> · DPA sebelumnya: {{ result.dpa_sebelumnya }}</span>
                <span v-if="realisasiDisalinText"> · realisasi {{ realisasiDisalinText }} disalin</span>
                <span v-if="result.sumber_bulan_mulai"> · bulan mulai: {{ result.bulan_mulai }} ({{ result.sumber_bulan_mulai }})</span>
              </div>

              <v-alert v-if="result.dry_run && result.total_errors > 0" type="error" dense text>
                Ada {{ fmt(result.total_errors) }} baris error. Perbaiki file Excel lalu unggah ulang.
                Tombol Proses Import nonaktif.
              </v-alert>
              <v-alert v-else-if="result.dry_run" type="success" dense text>
                {{ message.success || 'Dry run selesai, data siap diimport.' }}
              </v-alert>
              <v-alert v-else type="success" dense text>{{ message.success }}</v-alert>

              <v-simple-table dense class="mb-4">
                <thead>
                  <tr>
                    <th>Level</th><th class="text-right">Insert</th><th class="text-right">Update</th>
                    <th class="text-right">dihapus_dpa</th><th class="text-right">Tidak ada di Excel</th>
                    <th class="text-right">Duplikat DB</th><th class="text-right">Realisasi disalin</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="lvl in levels" :key="lvl">
                    <td>{{ lvl }}</td>
                    <td class="text-right">{{ fmt(ringkas(lvl, 'insert')) }}</td>
                    <td class="text-right">{{ fmt(ringkas(lvl, 'update')) }}</td>
                    <td class="text-right">{{ fmt(ringkas(lvl, 'dihapus_dpa')) }}</td>
                    <td class="text-right">{{ fmt(ringkas(lvl, 'tidak_ada_di_excel')) }}</td>
                    <td class="text-right">{{ fmt(ringkas(lvl, 'duplikat_db')) }}</td>
                    <td class="text-right">{{ fmt(ringkas(lvl, 'realisasi_disalin')) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Errors -->
              <div v-if="errorList.length" class="mb-4">
                <div class="body-1 font-weight-bold mb-1">
                  Error <v-chip x-small color="red lighten-4" class="ml-1">{{ fmt(result.total_errors) }}</v-chip>
                  <span v-if="result.total_errors > errorList.length" class="caption grey--text ml-2">
                    (menampilkan {{ fmt(errorList.length) }} pertama)
                  </span>
                </div>
                <v-simple-table dense fixed-header height="300">
                  <thead><tr><th>Baris</th><th>Kode Unit</th><th>Kode Sub Kegiatan</th><th>Pesan</th></tr></thead>
                  <tbody>
                    <tr v-for="(e, i) in errorList" :key="'e' + i">
                      <td>{{ e.baris }}</td><td>{{ e.kode_unit }}</td>
                      <td>{{ e.kode_sub_kegiatan }}</td><td>{{ e.pesan }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <!-- Warnings -->
              <div v-if="warningList.length" class="mb-4">
                <div class="body-1 font-weight-bold mb-1">
                  Peringatan <v-chip x-small color="amber lighten-4" class="ml-1">{{ fmt(warningList.length) }}</v-chip>
                  <span class="caption grey--text ml-2">tidak memblokir import</span>
                </div>
                <v-simple-table dense fixed-header height="220">
                  <thead><tr><th>Baris</th><th>Kode Sub Kegiatan</th><th>Pesan</th></tr></thead>
                  <tbody>
                    <tr v-for="(w, i) in warningList" :key="'w' + i">
                      <td>{{ w.baris }}</td><td>{{ w.kode_sub_kegiatan }}</td><td>{{ w.pesan }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <!-- Panel lipat -->
              <v-expansion-panels flat accordion class="mb-4" v-if="hasTidakAda || hasDuplikat">
                <v-expansion-panel v-if="hasTidakAda">
                  <v-expansion-panel-header>Task yang akan ditandai dihapus (tidak ada di Excel)</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-for="lvl in levels" :key="'t' + lvl">
                      <div v-if="listOf('tidak_ada_di_excel', lvl).length" class="mb-2">
                        <div class="font-weight-bold body-2">{{ lvl }} ({{ listOf('tidak_ada_di_excel', lvl).length }})</div>
                        <v-simple-table dense>
                          <thead><tr><th>Task ID</th><th>Unit ID</th><th>Kode</th><th>Nama</th></tr></thead>
                          <tbody>
                            <tr v-for="t in listOf('tidak_ada_di_excel', lvl)" :key="'ta' + lvl + t.task_id">
                              <td>{{ t.task_id }}</td><td>{{ t.unit_id }}</td><td>{{ t.kode }}</td><td>{{ t.nama }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel v-if="hasDuplikat">
                  <v-expansion-panel-header>Duplikat di database</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-for="lvl in levels" :key="'d' + lvl">
                      <div v-if="listOf('duplikat_db', lvl).length" class="mb-2">
                        <div class="font-weight-bold body-2">{{ lvl }} ({{ listOf('duplikat_db', lvl).length }})</div>
                        <pre class="caption" style="white-space: pre-wrap;">{{ JSON.stringify(listOf('duplikat_db', lvl), null, 2) }}</pre>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <div class="d-flex justify-end">
                <v-btn outlined class="text-capitalize mr-2" :disabled="busy" @click="unggahUlang">
                  {{ result.dry_run ? 'Unggah Ulang' : 'Selesai' }}
                </v-btn>
                <v-btn v-if="result.dry_run" color="primary" elevation="0" class="text-capitalize"
                  :disabled="busy || result.total_errors > 0 || !canSubmit" @click="dialog.confirm = true">
                  Proses Import
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- ZONA BERBAHAYA -->
          <v-card flat class="mb-6" style="border-radius: 10px; border: 1px solid #F3B8B8; background: #FFF5F5;">
            <v-card-text>
              <div class="body-1 font-weight-bold red--text text--darken-2">Zona Berbahaya</div>
              <div class="body-2 grey--text text--darken-1 mb-3">
                Hapus semua data tahun + status DPA terpilih (dibackup), atau kembalikan backup terakhir.
              </div>
              <v-btn color="red darken-2" dark elevation="0" class="text-capitalize mr-2"
                :disabled="busy || !form.tahun || !form.dpa_status" @click="openDanger('reset')">
                Reset Data {{ scopeKey }}
              </v-btn>
              <v-btn outlined class="text-capitalize" :disabled="busy || !form.tahun || !form.dpa_status"
                @click="openDanger('restore')">
                Restore Backup
              </v-btn>
              <v-alert v-if="danger.result" :type="danger.resultType" dense text class="mt-4 mb-0">
                {{ danger.result }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </section>

    <!-- Konfirmasi import -->
    <v-dialog v-model="dialog.confirm" max-width="460" persistent>
      <v-card class="pa-2" style="border-radius: 10px;">
        <v-card-title class="title">Proses Import?</v-card-title>
        <v-card-text>
          <v-alert type="warning" dense text class="mb-0">
            Data {{ scopeKey }} akan ditulis ke database: {{ fmt(totals.insert) }} insert,
            {{ fmt(totals.update) }} update, {{ fmt(totals.hapus) }} ditandai dihapus
            (tidak ada di Excel). Proses bisa beberapa menit.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-capitalize" :disabled="busy" @click="dialog.confirm = false">Batal</v-btn>
          <v-btn color="green darken-2" dark elevation="0" class="text-capitalize" :disabled="busy"
            @click="runImport">Ya, Proses Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reset / Restore -->
    <v-dialog v-model="dialog.danger" max-width="560" persistent>
      <v-card class="pa-2" style="border-radius: 10px; border-top: 4px solid #B91C1C;">
        <v-card-title class="title red--text text--darken-2">
          {{ danger.mode === 'reset' ? 'Reset Data' : 'Restore Backup' }} {{ scopeKey }}
        </v-card-title>
        <v-card-text>
          <div v-if="danger.mode === 'reset'" class="body-2 mb-3">
            Semua task tahun {{ danger.scope.tahun }} status {{ danger.scope.dpa_status }} akan
            DIHAPUS. Data dibackup dan bisa dikembalikan lewat Restore.
          </div>

          <v-alert v-if="danger.error" type="error" dense text>{{ danger.error }}</v-alert>

          <div v-if="danger.preview">
            <v-simple-table v-if="danger.preview.per_level" dense class="mb-3">
              <thead><tr><th>Level</th><th class="text-right">Total</th><th class="text-right">dihapus</th></tr></thead>
              <tbody>
                <tr v-for="p in danger.preview.per_level" :key="p.task_type">
                  <td>{{ p.task_type }}</td><td class="text-right">{{ fmt(p.total) }}</td>
                  <td class="text-right">{{ fmt(p.dihapus) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-if="danger.mode === 'reset' && danger.preview.referensi" class="mb-3">
              <v-alert :type="danger.preview.referensi.total_blocking > 0 ? 'error' : 'success'" dense text>
                Referensi capaian/pengajuan: {{ fmt(danger.preview.referensi.total_blocking) }} ·
                {{ danger.preview.referensi.total_blocking > 0 ? 'reset akan ditolak' : 'aman untuk direset' }}
              </v-alert>
            </div>
            <div v-if="danger.mode === 'restore'" class="body-2 mb-3">
              Batch: <b>{{ danger.preview.batch_id }}</b> · {{ fmt(danger.preview.total) }} task ·
              reset oleh {{ danger.preview.reset_oleh }} · {{ danger.preview.reset_at }}<br />
              Task di scope saat ini: {{ fmt(danger.preview.task_scope_saat_ini) }} ·
              task_id bentrok: {{ fmt(danger.preview.task_id_bentrok) }}
            </div>
          </div>

          <div class="body-2 mb-2">
            Ketik <code>{{ scopeKey }}</code> untuk konfirmasi
          </div>
          <v-text-field filled rounded dense hide-details :color="set_color" v-model="danger.konfirmasi"
            :disabled="busy" autocomplete="off" @paste.prevent @keyup.enter="confirmDanger"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-capitalize" :disabled="busy" @click="closeDanger">Batal</v-btn>
          <v-btn color="red darken-2" dark elevation="0" class="text-capitalize"
            :disabled="busy || !danger.loaded || danger.konfirmasi !== scopeKeyDanger" @click="confirmDanger">
            {{ danger.mode === 'reset' ? 'Hapus & Backup' : 'Restore' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { TokenService } from '@/service/Storage.Service'

const LEVELS = ['Program', 'Kegiatan', 'Sub Kegiatan']
const BULAN = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember']

export default {
  data () {
    return {
      levels: LEVELS,
      dpaStatuses: [
        { text: 'murni', value: 'murni' },
        { text: 'p1', value: 'p1' },
        { text: 'perubahan', value: 'perubahan' }
      ],
      bulanItems: [{ text: 'Otomatis (dari tanggal DPA)', value: '' }].concat(BULAN.map((t, i) => ({ text: t, value: i + 1 }))),

      form: { tahun: new Date().getFullYear(), dpa_status: 'murni', bulan_mulai: '' },
      file: null,
      dragging: false,

      // Scope yang dipakai saat dry run; bila form/file berubah, hasil dry run tidak valid lagi.
      result: null,
      snapshot: null,

      busy: false,
      message: { error: '', success: '' },

      dialog: { confirm: false, danger: false },
      danger: {
        mode: 'reset',
        scope: { tahun: '', dpa_status: '' },
        konfirmasi: '',
        preview: null,
        loaded: false,
        error: '',
        result: '',
        resultType: 'success'
      }
    }
  },
  computed: {
    set_color () { return this.$store.state.color },
    user () { return this.$store.state.user },
    years () {
      const max = new Date().getFullYear() + 1
      const arr = []
      for (let i = max; i >= max - 6; i--) arr.push({ text: i, value: i })
      return arr
    },
    scopeKey () { return `${this.form.tahun}-${this.form.dpa_status}` },
    scopeKeyDanger () { return `${this.danger.scope.tahun}-${this.danger.scope.dpa_status}` },
    canSubmit () { return !!(this.form.tahun && this.form.dpa_status && this.file) },
    fileSize () {
      if (!this.file) return ''
      const s = this.file.size
      return s > 1048576 ? (s / 1048576).toFixed(1).replace('.', ',') + ' MB' : Math.ceil(s / 1024) + ' KB'
    },
    errorList () { return (this.result && this.result.errors) || [] },
    warningList () { return (this.result && this.result.warnings) || [] },
    realisasiDisalinText () {
      const b = this.result && this.result.bulan_realisasi_disalin
      if (!b || !b.length) return ''
      return b.length > 1 ? `${BULAN[b[0] - 1].slice(0, 3)}–${BULAN[b[b.length - 1] - 1].slice(0, 3)}` : BULAN[b[0] - 1]
    },
    hasTidakAda () { return this.levels.some(l => this.listOf('tidak_ada_di_excel', l).length) },
    hasDuplikat () { return this.levels.some(l => this.listOf('duplikat_db', l).length) },
    totals () {
      const t = { insert: 0, update: 0, hapus: 0 }
      if (!this.result) return t
      this.levels.forEach(l => {
        t.insert += this.ringkas(l, 'insert')
        t.update += this.ringkas(l, 'update')
        t.hapus += this.ringkas(l, 'tidak_ada_di_excel')
      })
      return t
    }
  },
  created () {
    // Server tetap mengecek role (403); ini hanya mencegah halaman terbuka lewat URL langsung
    if (this.user.role !== 'admin') this.$router.replace('/')
  },
  watch: {
    // Tahun / status / bulan mulai / file berubah → wajib dry run ulang
    'form.tahun' () { this.invalidate() },
    'form.dpa_status' (v) { if (v === 'murni') this.form.bulan_mulai = ''; this.invalidate() },
    'form.bulan_mulai' () { this.invalidate() },
    file () { this.invalidate() }
  },
  methods: {
    fmt (n) { return Number(n || 0).toLocaleString('id-ID') },
    ringkas (lvl, key) {
      const r = this.result && this.result.ringkasan && this.result.ringkasan[lvl]
      return r ? Number(r[key] || 0) : 0
    },
    listOf (key, lvl) {
      const o = this.result && this.result[key]
      return (o && o[lvl]) || []
    },
    invalidate () {
      if (this.result && this.result.dry_run) { this.result = null; this.snapshot = null }
    },

    // ---------- HTTP ----------
    headers () {
      return { Authorization: process.env.VUE_APP_XAUTH, Token: TokenService.getToken() }
    },
    handleUnauthorized (err) {
      if (err && err.response && err.response.status === 401) {
        TokenService.removeToken()
        window.location = '/'
        return true
      }
      return false
    },
    // Respons JSON selalu HTTP 200; status sebenarnya di body.status
    async postForm (path, fd) {
      try {
        const { data } = await axios.post(`${process.env.VUE_APP_API_URL}${path}`, fd, {
          headers: this.headers(), // jangan set Content-Type manual
          timeout: 0
        })
        return data
      } catch (err) {
        if (this.handleUnauthorized(err)) return { status: 401, message: 'Unauthorized' }
        return { status: 0, message: 'Gagal terhubung ke server. Periksa koneksi lalu coba lagi.' }
      }
    },

    // ---------- Template ----------
    async unduhTemplate () {
      this.message.error = ''
      this.busy = true
      try {
        const res = await fetch(`${process.env.VUE_APP_API_URL}admin/import/anggaran/template`, { headers: this.headers() })
        if (res.status === 401) { TokenService.removeToken(); window.location = '/'; return }
        if ((res.headers.get('Content-Type') || '').includes('application/json')) {
          const body = await res.json()
          throw new Error(body.message)
        }
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = 'template_import_anggaran_dpa.xlsx'
        a.click()
        URL.revokeObjectURL(a.href)
      } catch (e) {
        this.message.error = e.message || 'Gagal mengunduh template.'
      } finally {
        this.busy = false
      }
    },

    // ---------- File ----------
    setFile (f) {
      this.message.error = ''
      if (!f) return
      if (!/\.(xlsx|xls)$/i.test(f.name)) {
        this.message.error = 'File harus .xlsx atau .xls.'
        return
      }
      this.file = f
    },
    onPick (e) { this.setFile(e.target.files[0]); e.target.value = '' },
    onDrop (e) { this.dragging = false; if (!this.busy) this.setFile(e.dataTransfer.files[0]) },
    unggahUlang () {
      this.result = null
      this.snapshot = null
      this.message = { error: '', success: '' }
      this.file = null
    },

    // ---------- Import ----------
    buildImportForm (dryRun) {
      const fd = new FormData()
      fd.append('tahun', this.form.tahun)
      fd.append('dpa_status', this.form.dpa_status)
      fd.append('dry_run_bool', dryRun ? 'true' : 'false') // hanya 'false' yang menulis data
      if (this.form.bulan_mulai) fd.append('bulan_mulai', this.form.bulan_mulai)
      fd.append('file', this.file)
      return fd
    },
    showFieldErrors (res) {
      // 412: results.data = objek error per field
      const d = res.results && res.results.data
      if (d && typeof d === 'object') {
        const detail = Object.values(d).filter(v => typeof v === 'string').join('; ')
        return detail ? `${res.message} ${detail}` : res.message
      }
      return res.message
    },
    async runDryRun () {
      if (this.busy || !this.canSubmit) return
      this.message = { error: '', success: '' }
      this.result = null
      this.busy = true
      const res = await this.postForm('admin/import/anggaran', this.buildImportForm(true))
      this.busy = false

      if (res.status === 200 && res.results) {
        this.result = res.results.data
        this.snapshot = this.scopeKey
        this.message.success = res.message
      } else if (res.status === 400 && res.results && res.results.data && res.results.data.errors) {
        this.result = res.results.data
        this.message.error = res.message
      } else {
        this.message.error = this.showFieldErrors(res)
      }
    },
    async runImport () {
      if (this.busy || !this.result || this.result.total_errors > 0) return
      this.busy = true
      const res = await this.postForm('admin/import/anggaran', this.buildImportForm(false))
      this.busy = false
      this.dialog.confirm = false

      if (res.status === 200 && res.results) {
        this.result = res.results.data
        this.message.success = res.message
        this.message.error = ''
      } else if (res.status === 400 && res.results && res.results.data && res.results.data.errors) {
        // Masih ada error baris: tampilkan tabel error yang sama
        this.result = res.results.data
        this.message.error = res.message
      } else {
        this.message.error = this.showFieldErrors(res)
      }
    },

    // ---------- Reset / Restore ----------
    async openDanger (mode) {
      this.danger = {
        mode,
        scope: { tahun: this.form.tahun, dpa_status: this.form.dpa_status },
        konfirmasi: '',
        preview: null,
        loaded: false,
        error: '',
        result: '',
        resultType: 'success'
      }
      this.dialog.danger = true
      // Langkah 1: dry run + konfirmasi otomatis untuk pratinjau jumlah
      this.busy = true
      const res = await this.postForm(`admin/import/anggaran/${mode}`, this.dangerForm(true, this.scopeKeyDanger))
      this.busy = false
      if (res.status === 200 && res.results) {
        this.danger.preview = res.results.data
        this.danger.loaded = true
        if (mode === 'reset' && res.message) this.danger.error = ''
      } else {
        this.danger.error = this.showFieldErrors(res)
      }
    },
    dangerForm (dryRun, konfirmasi) {
      const fd = new FormData()
      fd.append('tahun', this.danger.scope.tahun)
      fd.append('dpa_status', this.danger.scope.dpa_status)
      fd.append('konfirmasi', konfirmasi)
      fd.append('dry_run_bool', dryRun ? 'true' : 'false')
      if (this.danger.mode === 'restore' && this.danger.preview && this.danger.preview.batch_id) {
        fd.append('batch_id', this.danger.preview.batch_id)
      }
      return fd
    },
    closeDanger () { if (!this.busy) this.dialog.danger = false },
    async confirmDanger () {
      // Langkah 2: konfirmasi diketik pengguna sendiri → dry_run_bool=false
      if (this.busy || this.danger.konfirmasi !== this.scopeKeyDanger) return
      this.busy = true
      const res = await this.postForm(`admin/import/anggaran/${this.danger.mode}`, this.dangerForm(false, this.danger.konfirmasi))
      this.busy = false
      if (res.status === 200) {
        this.dialog.danger = false
        this.danger.resultType = 'success'
        this.danger.result = res.message
        // Data berubah: hasil dry run sebelumnya tidak berlaku lagi
        this.result = null
        this.snapshot = null
      } else {
        this.danger.error = this.showFieldErrors(res)
      }
    }
  }
}
</script>
